// S2-CC6: Poll eval jobs list every 5 seconds
import { useState, useEffect, useCallback } from 'react';
import { apiFetch, apiPost } from '../utils/api';

export interface EvalJob {
  job_id: string;
  type: string;
  status: string;
  agent_id: string;
  params: Record<string, unknown>;
  progress: Record<string, unknown> | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export function useJobs(pollInterval = 5000) {
  const [jobs, setJobs] = useState<EvalJob[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    apiFetch<EvalJob[]>('/v1/eval/jobs')
      .then(setJobs)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed'));
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, pollInterval);
    return () => clearInterval(timer);
  }, [refresh, pollInterval]);

  const cancelJob = useCallback(async (jobId: string) => {
    await apiPost(`/v1/eval/jobs/${jobId}/cancel`, {});
    refresh();
  }, [refresh]);

  const cancelAll = useCallback(async () => {
    await apiPost('/v1/eval/jobs/cancel-all', {});
    refresh();
  }, [refresh]);

  const deleteJob = useCallback(async (jobId: string) => {
    await apiFetch(`/v1/eval/jobs/${jobId}`, { method: 'DELETE' });
    refresh();
  }, [refresh]);

  const purgeJobs = useCallback(async () => {
    await apiPost('/v1/eval/jobs/purge', {});
    refresh();
  }, [refresh]);

  return { jobs, error, refresh, cancelJob, cancelAll, deleteJob, purgeJobs };
}
